import type { AxisSelection } from '@atlas/types'
import { type ObjectTag, RuleElementType } from '@atlas/types'
import { toRuleListItems } from '@/adapters/rule-list-item'
import { useApplicableRules } from '@/hooks/useExplore'
import { useRuleEditing } from '@/hooks/useRuleEditing'

/** A message's dimension tags → the engine's context tuple. */
const toContext = (tags: ObjectTag[]): AxisSelection => {
	const context: AxisSelection = {}
	for (const t of tags) context[t.axisCode] = [...(context[t.axisCode] ?? []), t.valueCode]
	return context
}

/**
 * The rules governing a message's text, plus whether the current user may edit them.
 *
 * Resolution passes element type `text`: these are the rules that reach the
 * generation step for this message, so rules governing only video or imagery would
 * just dilute it. Rules declaring no element types still apply.
 *
 * Nothing here is message-specific except that narrowing and the tuple: resolution and
 * editing are the shared hooks every resolved-rule surface uses.
 */
export function useMessageRules(messageId: string | null, tags: ObjectTag[]) {
	const { data: rules = [], isLoading } = useApplicableRules(toContext(tags), !!messageId, RuleElementType.TEXT)
	const editing = useRuleEditing()

	return { rules: toRuleListItems(rules), isLoading, ...editing }
}
